import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import './Toast.css';

const ToastContext = createContext();

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now();
    setToasts(prev => [...prev, { ...toast, id }]);
    
    // Auto remove after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, toast.duration || 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {createPortal(
        <div className="agrisense-toast-container">
          {toasts.map(toast => (
            <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

function Toast({ type, title, message, onClose }) {
  const { t } = useTranslation();
  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertTriangle,
    info: Info,
  };

  const Icon = icons[type];

  const toastClasses = classNames(
    'agrisense-toast',
    `agrisense-toast--${type}`
  );

  return (
    <div className={toastClasses} data-testid="toast">
      <div className="agrisense-toast__icon-container">
        <Icon size={20} />
      </div>
      <div className="agrisense-toast__content">
        <div className="agrisense-toast__title">{title}</div>
        {message && (
          <div className="agrisense-toast__message">{message}</div>
        )}
      </div>
      <button 
        className="agrisense-toast__close"
        onClick={onClose}
        aria-label={t('common.a11y.closeToast')}
        data-testid="toast-close"
      >
        <X size={16} />
      </button>
    </div>
  );
}
